import React, { CSSProperties, FC, useMemo } from 'react'
import dynamic from 'next/dynamic'
import BookingCalendar, { type BookingFormFields } from '@hermes/booking-calendar'
import { useBookingCalendar } from '../../providers/BookingCalendar'
import { useWpaTenant } from '../../hooks/useWpaTenant'
import { useMediaQuery, useTheme } from '@hermes/web-components'

const InsuranceModal = dynamic(() => import('./InsuranceModal'), {
  ssr: false
})

interface Props {
  containerStyle?: CSSProperties
  onBackButtonClick?: () => void
}

const BookingCalendarApp: FC<Props> = ({ containerStyle, onBackButtonClick = () => null }) => {
  const { isWPA } = useWpaTenant()
  const bookingCalendarContext = useBookingCalendar()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {
    state,
    isFullScreenMode,
    countPrfStepValue,
    isInsuranceModalOpen,
    displayPracticeSelector,
    changeScreen,
    handleBookButtonClick,
    handleExpandButtonClick,
    handleContactRequestClick,
    handleDateSelectionClick,
    handleTimeSelectionClick,
    handleProfileSelectionClick,
    handleSlotNotAvailableBackClick,
    handleSlotSelectionClick,
    handleInsuranceSelectClick,
    handleShowMoreDatesClick,
    handleShowMoreSlotsClick,
    handleViewAllAvailabilityClick,
    insurances
  } = bookingCalendarContext

  const {
    currentScreen,
    slots,
    specialistProfile,
    practiceProfile,
    visitReasons,
    selectedInsurance,
    selectedDateString,
    selectedTimeString
  } = state

  const handleBackButtonClick = () => {
    if (specialistProfile) {
      window.dataLayer.push({
        event: 'prf_pg_book_calndr_interaction',
        cont_prf_name: specialistProfile.name ?? '',
        cont_prf_type: 'Specialist',
        cont_prf_uuid: specialistProfile.externalId,
        cont_interaction_label: 'back button click',
        cont_prf_step: countPrfStepValue
      })
    }

    onBackButtonClick()
  }

  const formFields: BookingFormFields = useMemo(
    () =>
      isWPA
        ? [
            'visitReason',
            'firstName',
            'lastName',
            'dob',
            'gender',
            'phoneNumber',
            'email',
            'addressLine',
            'city',
            'postalCode',
            'claimNumber',
            'policyNumber',
            'employeeId',
            'agreement'
          ]
        : [
            'bookingFor',
            'visitReason',
            'title',
            'firstName',
            'lastName',
            'dob',
            'gender',
            'email',
            'addressLine1',
            'addressLine2',
            'city',
            'postalCode',
            'phoneNumber',
            'paymentMethod',
            'agreement'
          ],
    [isWPA]
  )

  const bookingSource = useMemo(() => (isMobile ? 'Mobile' : 'Desktop'), [isMobile])
  const bookingTheme = useMemo(() => (isWPA ? 'wpa' : 'default'), [isWPA])

  return (
    <>
      <BookingCalendar
        selectedInsurance={selectedInsurance}
        selectedDateString={selectedDateString}
        selectedTimeString={selectedTimeString}
        containerStyle={containerStyle}
        currentScreen={currentScreen}
        slots={slots}
        specialistProfile={specialistProfile}
        practiceProfile={practiceProfile}
        visitReasons={visitReasons}
        expandedView={isFullScreenMode}
        onChangeScreen={changeScreen}
        onInsuranceSelectClick={handleInsuranceSelectClick}
        onBookButtonClick={handleBookButtonClick}
        onCalendarExpandClick={handleExpandButtonClick}
        onContactRequestClick={handleContactRequestClick}
        onDateSelectionClick={handleDateSelectionClick}
        onTimeSelectionClick={handleTimeSelectionClick}
        onProfileSelectionClick={handleProfileSelectionClick}
        onSlotNotAvailableBackClick={handleSlotNotAvailableBackClick}
        onSlotSelectionClick={handleSlotSelectionClick}
        onBackButtonClick={handleBackButtonClick}
        onShowMoreDatesClick={handleShowMoreDatesClick}
        onShowMoreSlotsClick={handleShowMoreSlotsClick}
        onViewAllAvailabilityClick={handleViewAllAvailabilityClick}
        displayMobileContactPanel={!isWPA}
        displayLocationDropdown={displayPracticeSelector}
        formFields={formFields}
        availabilityId={state.selectedSlot?.id}
        provider={state.selectedSlot?.provider}
        source={bookingSource}
        theme={bookingTheme}
        insurances={isWPA ? [] : insurances}
        hideTitle={isWPA}
      />
      {isInsuranceModalOpen ? <InsuranceModal isOpen onClose={handleInsuranceSelectClick} /> : null}
    </>
  )
}

export default BookingCalendarApp
